import { ChainId } from '@src/configs/chainInfo'
import { Address } from '@src/types/token'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('URLParams')

/**
 * Parse the from/to token format (chainId:tokenAddress)
 *
 * @param param - The token parameter string in format "chainId:tokenAddress"
 * @returns Parsed token information or null if invalid
 *
 * @remarks
 * This function supports both EVM addresses (0x-prefixed) and non-EVM addresses.
 * Minimal validation is performed to allow for different address formats across chains.
 */
export const parseTokenParam = (param: string | null): { chainId: number; tokenAddress: Address } | null => {
    if (!param) return null

    const parts = param.split(':')
    if (parts.length !== 2) return null

    try {
        const chainId = parseInt(parts[0], 10)
        const tokenAddress = parts[1] as Address

        // Validate that chainId is a valid chain ID
        if (isNaN(chainId) || !Object.values(ChainId).includes(chainId)) {
            return null
        }

        // Basic validation - just ensure the address isn't empty
        if (!tokenAddress || tokenAddress.trim() === '') {
            return null
        }

        return { chainId, tokenAddress }
    } catch (error) {
        logger.error('Error parsing token parameter:', error)
        return null
    }
}

/**
 * Parse chains parameter from URL
 *
 * @param chainsParam - The chains parameter string, either a single chain ID or comma-separated list
 * @returns Array of valid chain IDs or null if none are valid
 */
export const parseChains = (chainsParam: string | null): number[] | null => {
    if (!chainsParam) return null

    try {
        // Handle the case where it's a single number (no commas)
        if (!chainsParam.includes(',')) {
            const chainId = parseInt(chainsParam.trim(), 10)
            logger.debug(`Parsing single chain parameter: ${chainsParam} -> ${chainId}`)

            if (!isNaN(chainId)) {
                // Check if this is a valid chain ID
                if (Object.values(ChainId).includes(chainId)) {
                    logger.debug(`Valid single chain ID found: ${chainId}`)
                    return [chainId]
                } else {
                    logger.debug(`Chain ID not recognized: ${chainId}`)
                }
            }
        }

        // Handle comma-separated list
        const chains = chainsParam.split(',').map(chain => parseInt(chain.trim(), 10))
        const validChains = chains.filter(chainId => !isNaN(chainId) && Object.values(ChainId).includes(chainId))

        logger.debug(`Parsing chains parameter: ${chainsParam} -> ${JSON.stringify(validChains)}`)

        return validChains.length > 0 ? validChains : null
    } catch (error) {
        logger.error('Error parsing chains parameter:', error)
        return null
    }
}
